import type { ApiError } from './types';

/**
 * Axios's default `error.message` for a failed request is the useless
 * "Request failed with status code 400". The response interceptor already
 * replaces thrown errors with a normalized {@link ApiError} whose `message`
 * holds the real backend detail (see `normalizeApiError`), so this helper
 * mostly reads that — with a validation-errors fallback and guards so it can
 * be called on any thrown value.
 */

const GENERIC_AXIOS_MESSAGE = /^Request failed with status code \d+$/i;

function firstValidationMessage(errors: unknown): string | undefined {
  if (typeof errors !== 'object' || errors === null) return undefined;
  for (const value of Object.values(errors as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const first = value.find((item) => typeof item === 'string' && item.trim());
      if (typeof first === 'string') return first.trim();
    } else if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

/**
 * Best-effort human-readable message from any thrown value. Returns
 * `undefined` when nothing meaningful is available so callers can fall back
 * to their own copy (e.g. a translated generic message).
 */
export function extractApiErrorMessage(error: unknown): string | undefined {
  if (error == null) return undefined;

  if (typeof error === 'string') {
    const trimmed = error.trim();
    return trimmed || undefined;
  }

  if (typeof error === 'object') {
    const candidate = error as Partial<ApiError> & { message?: unknown; errors?: unknown };

    if (typeof candidate.message === 'string') {
      const message = candidate.message.trim();
      if (message && !GENERIC_AXIOS_MESSAGE.test(message)) return message;
    }

    const validation = firstValidationMessage(candidate.errors);
    if (validation) return validation;
  }

  return undefined;
}

export function extractApiFieldErrors(error: unknown): Record<string, string> {
  if (typeof error !== 'object' || error === null) return {};
  const errors = (error as { errors?: unknown }).errors;
  if (typeof errors !== 'object' || errors === null) return {};

  return Object.entries(errors as Record<string, unknown>).reduce<Record<string, string>>((result, [field, value]) => {
    const message = Array.isArray(value)
      ? value.find((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : typeof value === 'string' ? value : undefined;
    if (message) result[field.charAt(0).toLowerCase() + field.slice(1)] = message.trim();
    return result;
  }, {});
}
